/**
 * Social Campaign Section - Dynamic Rendering Engine
 * ===================================================
 *
 * This script handles conditional rendering of Social Campaign sections
 * based on URL parameters, login state, and customer segments.
 *
 * Features:
 * - Supports multiple campaign sections on a single page
 * - Each section evaluates conditions independently
 * - No cross-section interference or global state blocking
 * - Two rendering modes:
 *   1. Template Mode: Uses template cloning for social-campaign.liquid
 *   2. Show/Hide Mode: Simple visibility toggle for other sections
 * - Fires dataLayer events for analytics tracking
 * - Session persistence across customer journey
 *
 * Data Attributes (per section):
 * - data-social-campaign-section: Section wrapper marker
 * - data-section-id: Unique section identifier
 * - data-campaign-param-name: URL parameter name to check
 * - data-campaign-param-value: Expected parameter value
 * - data-campaign-match: Match type (equals|contains)
 * - data-require-login: Whether login is required (true|false)
 * - data-campaign-segment: Customer tag to validate
 *
 * Rendering Modes:
 * - If section has [data-social-campaign-template] and [data-social-campaign-placeholder]:
 *   Uses template cloning (social-campaign.liquid behavior)
 * - Otherwise: Uses show/hide visibility toggle
 *   Sections should have CSS class "social-campaign-hidden" or inline style display:none
 *
 * Global Customer State (set by Liquid):
 * - window.ShopifyCustomer.loggedIn: boolean
 * - window.ShopifyCustomer.tags: array of customer tags
 */

(function () {
  'use strict';

  /**
   * Configuration
   */
  const CONFIG = {
    debug: false, // Set to true for console logging
    selectors: {
      section: '[data-social-campaign-section]',
      template: '[data-social-campaign-template]',
      placeholder: '[data-social-campaign-placeholder]',
    },
    dataLayerEvent: 'social_campaign_render',
    cookiePrefix: 'shoplc_campaign_', // Prefix for campaign cookies
  };

  /**
   * Get global session settings from window.SocialCampaignConfig
   * Falls back to defaults if not configured
   */
  function getGlobalSessionConfig() {
    const globalConfig = window.SocialCampaignConfig || {};
    return {
      paramName: globalConfig.paramName || 'utm_source',
      sessionEnabled: globalConfig.sessionEnabled !== false, // Default true
      sessionDays: globalConfig.sessionDays || 7,
      overwriteSession: globalConfig.overwriteSession !== false, // Default true
    };
  }

  // ============================================
  // COOKIE HELPER FUNCTIONS
  // ============================================

  /**
   * Set a cookie with specified name, value, and expiration days
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Number of days until expiration
   */
  function setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + ';' + expires + ';path=/;SameSite=Lax';
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value or null if not found
   */
  function getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Delete a cookie by name
   * @param {string} name - Cookie name
   */
  function deleteCookie(name) {
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
  }

  /**
   * Get the cookie name for a specific campaign parameter
   * @param {string} paramName - Campaign parameter name
   * @returns {string} - Full cookie name
   */
  function getCampaignCookieName(paramName) {
    return CONFIG.cookiePrefix + paramName;
  }

  /**
   * Store campaign session in cookie
   * @param {string} paramName - Campaign parameter name
   * @param {string} paramValue - Campaign parameter value
   * @param {number} days - Session duration in days
   */
  function storeCampaignSession(paramName, paramValue, days) {
    const cookieName = getCampaignCookieName(paramName);
    setCookie(cookieName, paramValue, days);
    debugLog('SESSION', 'Stored campaign session', {
      paramName,
      paramValue,
      expiresInDays: days,
    });
  }

  /**
   * Get stored campaign session from cookie
   * @param {string} paramName - Campaign parameter name
   * @returns {string|null} - Stored campaign value or null
   */
  function getCampaignSession(paramName) {
    const cookieName = getCampaignCookieName(paramName);
    return getCookie(cookieName);
  }

  /**
   * Clear campaign session cookie
   * @param {string} paramName - Campaign parameter name
   */
  function clearCampaignSession(paramName) {
    const cookieName = getCampaignCookieName(paramName);
    deleteCookie(cookieName);
    debugLog('SESSION', 'Cleared campaign session', { paramName });
  }

  // ============================================
  // URL PARAMETER FUNCTIONS
  // ============================================

  /**
   * Get URL parameter value
   * @param {string} paramName - Name of the URL parameter
   * @returns {string|null} - Parameter value or null if not found
   */
  function getUrlParam(paramName) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
  }

  /**
   * Get campaign value from URL or session
   * Handles session persistence logic
   * @param {string} paramName - Campaign parameter name
   * @param {boolean} sessionEnabled - Whether session persistence is enabled
   * @param {number} sessionDays - Session duration in days
   * @param {boolean} overwriteSession - Whether to overwrite existing session
   * @returns {string|null} - Campaign value from URL or session
   */
  function getCampaignValue(paramName, sessionEnabled, sessionDays, overwriteSession) {
    const urlValue = getUrlParam(paramName);
    const sessionValue = sessionEnabled ? getCampaignSession(paramName) : null;

    // Case 1: URL has campaign parameter
    if (urlValue) {
      if (sessionEnabled) {
        // Check if we should store/overwrite session
        if (!sessionValue || overwriteSession) {
          storeCampaignSession(paramName, urlValue, sessionDays);
        }
      }
      return urlValue;
    }

    // Case 2: No URL param but session exists
    if (sessionEnabled && sessionValue) {
      debugLog('SESSION', 'Using stored campaign session', {
        paramName,
        sessionValue,
      });
      return sessionValue;
    }

    // Case 3: No URL param and no session
    return null;
  }

  /**
   * Check if customer is logged in
   * @returns {boolean}
   */
  function isCustomerLoggedIn() {
    return window.ShopifyCustomer && window.ShopifyCustomer.loggedIn === true;
  }

  /**
   * Check if customer has a specific tag
   * @param {string} tag - Tag to check for
   * @returns {boolean}
   */
  function customerHasTag(tag) {
    if (!tag || tag.trim() === '') {
      return true; // No tag requirement = always pass
    }

    if (!window.ShopifyCustomer || !Array.isArray(window.ShopifyCustomer.tags)) {
      return false;
    }

    const normalizedTag = tag.toLowerCase().trim();
    return window.ShopifyCustomer.tags.some(
      (customerTag) => customerTag.toLowerCase().trim() === normalizedTag
    );
  }

  /**
   * Validate campaign parameter match
   * Supports multiple comma-separated values (e.g., "facebook, instagram")
   * Returns true if ANY of the expected values match
   * Now supports session persistence - checks both URL and stored session
   *
   * @param {string} actualValue - The actual campaign value (from URL or session)
   * @param {string} expectedValue - Expected value(s) to match (comma-separated)
   * @param {string} matchType - Match type: 'equals' or 'contains'
   * @returns {boolean}
   */
  function validateCampaignParam(actualValue, expectedValue, matchType) {
    // If no expected value configured, skip this check (pass)
    if (!expectedValue || expectedValue.trim() === '') {
      return true;
    }

    // No campaign value available (neither URL nor session)
    if (!actualValue) {
      return false;
    }

    const normalizedActual = actualValue.toLowerCase().trim();

    // Split expected values by comma and check each one
    // Supports: "facebook, instagram" or "facebook,instagram"
    const expectedValues = expectedValue
      .split(',')
      .map((val) => val.toLowerCase().trim())
      .filter((val) => val !== '');

    // Check if ANY expected value matches
    return expectedValues.some((expected) => {
      if (matchType === 'contains') {
        return normalizedActual.includes(expected);
      }
      // Default: equals (exact match)
      return normalizedActual === expected;
    });
  }

  /**
   * Log debug message
   * @param {string} sectionId - Section identifier
   * @param {string} message - Message to log
   * @param {*} data - Optional data to include
   */
  function debugLog(sectionId, message, data = null) {
    if (!CONFIG.debug) return;

    const prefix = `[Campaign] ${sectionId}:`;
    if (data !== null) {
      console.log(prefix, message, data);
    } else {
      console.log(prefix, message);
    }
  }

  /**
   * Push event to dataLayer
   * @param {string} sectionId - Section identifier
   * @param {string} campaignValue - Campaign parameter value
   */
  function pushDataLayerEvent(sectionId, campaignValue) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: CONFIG.dataLayerEvent,
      campaign_value: campaignValue || '',
      section_id: sectionId || '',
    });

    debugLog(sectionId, 'dataLayer event pushed', {
      event: CONFIG.dataLayerEvent,
      campaign_value: campaignValue,
      section_id: sectionId,
    });
  }

  /**
   * Process a single campaign section
   * @param {HTMLElement} section - Section element to process
   */
  function processSection(section) {
    if (section.classList.contains('social-campaign-section--rendered')) {
      return;
    }

    // Read section data attributes
    const sectionId = section.dataset.sectionId || 'unknown';
    const paramName = section.dataset.campaignParamName || 'utm_source';
    const paramValue = section.dataset.campaignParamValue || '';
    const matchType = section.dataset.campaignMatch || 'equals';
    const requireLogin = section.dataset.requireLogin === 'true';
    const customerSegment = section.dataset.campaignSegment || '';

    // Get global session persistence settings
    const globalConfig = getGlobalSessionConfig();
    const sessionEnabled = globalConfig.sessionEnabled;
    const sessionDays = globalConfig.sessionDays;
    const overwriteSession = globalConfig.overwriteSession;

    // Determine rendering mode
    const template = section.querySelector(CONFIG.selectors.template);
    const placeholder = section.querySelector(CONFIG.selectors.placeholder);
    const isTemplateMode = template && placeholder;

    debugLog(sectionId, 'Processing section', {
      paramName,
      paramValue,
      matchType,
      requireLogin,
      customerSegment,
      sessionEnabled,
      sessionDays,
      overwriteSession,
      mode: isTemplateMode ? 'template' : 'show/hide',
      source: 'Global Config',
    });

    // Get campaign value from URL or session (handles persistence logic)
    const actualCampaignValue = getCampaignValue(paramName, sessionEnabled, sessionDays, overwriteSession);
    const urlValue = getUrlParam(paramName);
    const sessionValue = sessionEnabled ? getCampaignSession(paramName) : null;

    debugLog(sectionId, 'Campaign value sources', {
      urlValue,
      sessionValue,
      usingValue: actualCampaignValue,
      source: urlValue ? 'URL' : sessionValue ? 'Session' : 'None',
    });

    // Condition 1: Validate campaign parameter (now uses URL or session value)
    const paramValid = validateCampaignParam(actualCampaignValue, paramValue, matchType);
    if (!paramValid) {
      debugLog(sectionId, 'FAILED: Campaign parameter mismatch', {
        expectedValues: paramValue.split(',').map((v) => v.trim()),
        actual: actualCampaignValue,
        source: urlValue ? 'URL' : sessionValue ? 'Session' : 'None',
        matchType,
      });
      hideSection(section, sectionId);
      return; // Exit - section will not render
    }
    debugLog(sectionId, 'PASSED: Campaign parameter', {
      matchedFrom: paramValue.split(',').map((v) => v.trim()),
      actual: actualCampaignValue,
      source: urlValue ? 'URL' : 'Session',
    });

    // Condition 2: Validate login requirement
    if (requireLogin && !isCustomerLoggedIn()) {
      debugLog(sectionId, 'FAILED: Login required but customer not logged in');
      hideSection(section, sectionId);
      return; // Exit - section will not render
    }
    debugLog(sectionId, 'PASSED: Login requirement');

    // Condition 3: Validate customer segment
    if (customerSegment && !customerHasTag(customerSegment)) {
      debugLog(sectionId, 'FAILED: Customer segment mismatch', {
        required: customerSegment,
        customerTags: window.ShopifyCustomer?.tags || [],
      });
      hideSection(section, sectionId);
      return; // Exit - section will not render
    }
    debugLog(sectionId, 'PASSED: Customer segment');

    // All conditions passed - render the section
    renderSection(section, sectionId, actualCampaignValue);
  }

  /**
   * cloneNode() does not execute <script> tags. Recreate executable scripts
   * after template content is inserted so campaign HTML can boot itself.
   * @param {HTMLElement} root
   */
  function activateClonedScripts(root) {
    if (!root) return;

    root.querySelectorAll('script').forEach((oldScript) => {
      const type = (oldScript.getAttribute('type') || 'text/javascript').toLowerCase();
      if (
        type &&
        type !== 'text/javascript' &&
        type !== 'application/javascript' &&
        type !== 'module'
      ) {
        return;
      }

      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }

      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /**
   * Render section content
   * Two modes:
   * 1. Template Mode: Clone template into placeholder (social-campaign.liquid)
   * 2. Show/Hide Mode: Simply make the section visible (other sections)
   *
   * @param {HTMLElement} section - Section element
   * @param {string} sectionId - Section identifier
   * @param {string} campaignValue - Campaign parameter value for tracking
   */
  function renderSection(section, sectionId, campaignValue) {
    const template = section.querySelector(CONFIG.selectors.template);
    const placeholder = section.querySelector(CONFIG.selectors.placeholder);

    // Check if this is a template-based section (social-campaign.liquid)
    const isTemplateMode = template && placeholder;

    if (isTemplateMode) {
      // Template Mode: Clone template content and inject into placeholder
      const content = template.content.cloneNode(true);
      placeholder.appendChild(content);
      activateClonedScripts(placeholder);
      debugLog(sectionId, 'RENDERED via template mode');
    } else {
      // Show/Hide Mode: Make the section visible
      section.style.display = '';
      section.classList.remove('social-campaign-hidden');
      section.removeAttribute('hidden');
      debugLog(sectionId, 'RENDERED via show/hide mode');
    }

    // Mark section as rendered (for potential CSS styling)
    section.classList.add('social-campaign-section--rendered');

    console.log('[Campaign]', sectionId, 'rendered');

    // Push analytics event
    pushDataLayerEvent(sectionId, campaignValue);
  }

  /**
   * Hide a section that failed condition checks
   * @param {HTMLElement} section - Section element
   * @param {string} sectionId - Section identifier
   */
  function hideSection(section, sectionId) {
    const template = section.querySelector(CONFIG.selectors.template);
    const placeholder = section.querySelector(CONFIG.selectors.placeholder);
    const isTemplateMode = template && placeholder;

    if (!isTemplateMode) {
      // Show/Hide Mode: Ensure the section stays hidden
      section.style.display = 'none';
      section.classList.add('social-campaign-hidden');
      debugLog(sectionId, 'Section hidden (conditions not met)');
    }
    // Template mode sections are already hidden by default (no content in placeholder)
  }

  /**
   * Initialize all campaign sections on the page
   */
  function initializeCampaignSections() {
    const sections = document.querySelectorAll(CONFIG.selectors.section);

    if (sections.length === 0) {
      return; // No campaign sections on this page
    }

    debugLog('INIT', `Found ${sections.length} campaign section(s)`);

    // First pass: Ensure all non-template sections are initially hidden
    // This prevents flash of content before conditions are evaluated
    sections.forEach((section) => {
      const template = section.querySelector(CONFIG.selectors.template);
      const placeholder = section.querySelector(CONFIG.selectors.placeholder);
      const isTemplateMode = template && placeholder;

      if (!isTemplateMode) {
        // Show/Hide mode: Ensure section is hidden initially
        // Only hide if not already processed (prevents re-hiding on re-init)
        if (!section.classList.contains('social-campaign-section--rendered')) {
          section.style.display = 'none';
          section.classList.add('social-campaign-hidden');
        }
      }
    });

    // Second pass: Process each section independently
    sections.forEach((section) => {
      try {
        processSection(section);
      } catch (error) {
        const sectionId = section.dataset.sectionId || 'unknown';
        console.error(`[Campaign] ${sectionId}: Error processing section`, error);
      }
    });
  }

  /**
   * Entry point - run on DOMContentLoaded
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeCampaignSections);
    } else {
      // DOM already loaded
      initializeCampaignSections();
    }
  }

  // ============================================
  // GLOBAL API EXPOSURE
  // ============================================

  /**
   * Expose session management functions globally
   * Allows external scripts to interact with campaign sessions
   */
  window.SocialCampaignSession = {
    /**
     * Get current campaign session value
     * @param {string} paramName - Campaign parameter name (default: from first section)
     * @returns {string|null}
     */
    get: function (paramName) {
      const name = paramName || getDefaultParamName();
      return getCampaignSession(name);
    },

    /**
     * Set campaign session value
     * @param {string} paramName - Campaign parameter name
     * @param {string} value - Campaign value to store
     * @param {number} days - Session duration in days (default: 7)
     */
    set: function (paramName, value, days) {
      storeCampaignSession(paramName, value, days || 7);
    },

    /**
     * Clear campaign session
     * @param {string} paramName - Campaign parameter name (clears specific param)
     */
    clear: function (paramName) {
      if (paramName) {
        clearCampaignSession(paramName);
      } else {
        // Clear all known campaign cookies
        const cookies = document.cookie.split(';');
        cookies.forEach(function (cookie) {
          const cookieName = cookie.split('=')[0].trim();
          if (cookieName.startsWith(CONFIG.cookiePrefix)) {
            deleteCookie(cookieName);
          }
        });
        debugLog('SESSION', 'Cleared all campaign sessions');
      }
    },

    /**
     * Check if a campaign session exists
     * @param {string} paramName - Campaign parameter name
     * @returns {boolean}
     */
    exists: function (paramName) {
      const name = paramName || getDefaultParamName();
      return getCampaignSession(name) !== null;
    },

    /**
     * Get all active campaign sessions
     * @returns {Object} - Object with paramName: value pairs
     */
    getAll: function () {
      const sessions = {};
      const cookies = document.cookie.split(';');
      cookies.forEach(function (cookie) {
        const parts = cookie.split('=');
        const cookieName = parts[0].trim();
        if (cookieName.startsWith(CONFIG.cookiePrefix)) {
          const paramName = cookieName.replace(CONFIG.cookiePrefix, '');
          sessions[paramName] = decodeURIComponent(parts[1] || '');
        }
      });
      return sessions;
    },
  };

  /**
   * Get default parameter name from first section on page
   * @returns {string}
   */
  function getDefaultParamName() {
    const section = document.querySelector(CONFIG.selectors.section);
    return section ? section.dataset.campaignParamName || 'utm_source' : 'utm_source';
  }

  // Start initialization
  init();
})();
